import type { InjectionKey } from 'vue';

const IMMERSIVE_ACTIVITY_KEY: InjectionKey<boolean> = Symbol('immersive-activity');

/**
 * Whether the page hosting an activity draws the new information architecture's chrome around it
 * (the bar, and the way back above the activity's overview). Such a host hands the screen to the
 * activity once the student starts it: the coach shell for a signed-in student, the public learn
 * pages for a guest.
 */
export const provideImmersiveActivity = (enabled: boolean) => provide(IMMERSIVE_ACTIVITY_KEY, enabled);

export const useImmersiveActivityEnabled = (): boolean => inject(IMMERSIVE_ACTIVITY_KEY, false);

/**
 * Whether the activity has taken the whole screen: past its overview, so the host's bar and back
 * button step aside and the activity draws its own close.
 */
export const useImmersiveActivity = () => useState('coach-immersive-activity', () => false);

// Owner ids for the screen claim, unique across every activity that may hold it.
let claims = 0;

interface ScreenClaim {
  /** The activity has loaded what it shows. */
  ready: () => boolean;
  /** The activity is on its overview, where the host's way back still shows. */
  onOverview: () => boolean;
  shellClosesActivity: boolean;
}

/**
 * An activity whose host lets it take the screen claims it once it is past its overview and gives it
 * back when it goes. `drawsOwnClose` is whether the activity shows its own close: always once it has
 * the screen, and otherwise only where neither the shell nor the host draws a way out.
 */
export const useActivityScreenClaim = ({ ready, onOverview, shellClosesActivity }: ScreenClaim) => {
  const canTakeScreen = useImmersiveActivityEnabled();
  const immersive = useImmersiveActivity();
  // Which activity holds the screen. Moving straight from one activity to the next mounts the new
  // one before the old one unmounts, so the old one may only give back a claim it still owns.
  const owner = useState<string | null>('coach-immersive-activity-owner', () => null);
  const id = `claim-${++claims}`;

  const release = () => {
    if (owner.value === id) {
      owner.value = null;
      immersive.value = false;
    }
  };

  watch(() => canTakeScreen && ready() && !onOverview(), (takesScreen) => {
    if (takesScreen) {
      owner.value = id;
      immersive.value = true;
      return;
    }
    release();
  }, { immediate: true });
  onUnmounted(release);

  const drawsOwnClose = computed(() => (immersive.value && owner.value === id) || (!shellClosesActivity && !canTakeScreen));

  return { drawsOwnClose };
};
